// AdminManualBookingGroupModal — single source of truth for admin manual
// bookings.  Handles both single-item and multi-item bookings (a 1-item
// booking is simply a group containing one child booking), partial sets,
// pickup / delivery, discounts, bond overrides, trial-lead linking, payment
// links and consolidated notifications.
//
// Backend endpoints (Phase 5A):
//   POST /api/admin/bookings/manual/preview   (live, debounced)
//   POST /api/admin/bookings/manual/create    (submit)
//   POST /api/admin/bookings/group/{id}/edit/preview  (edit-mode preview)
//   POST /api/admin/bookings/group/{id}/edit          (edit submit)
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Plus, Trash2, AlertCircle, Search, CheckCircle2, Package, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { nextDeliveryFeeOnMethodChange } from "./deliveryFeePolicy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

// ─── customer search ────────────────────────────────────────────────────────

function CustomerSearchBox({ onSelect, initialQuery = "", initialSelected = null }) {
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [selected, setSelected] = useState(initialSelected);
  const [showNew, setShowNew] = useState(false);
  const [newC, setNewC] = useState({ name: "", email: "", phone: "" });
  // Phase 5D Rev 3 — once the admin explicitly clears the selection ("Change"),
  // never let a fresh `initialSelected` object from the parent re-hydrate it.
  // Parents pass an inline object literal, so referential equality is a new
  // object on every render; without this sentinel the customer selector snaps
  // back to Kim and Save stays disabled.
  const userClearedRef = useRef(false);
  const lastInitialIdRef = useRef(initialSelected?.id || null);

  // Phase 5D — allow parent to swap in a pre-selected customer (edit mode),
  // but only when the underlying customer identity actually changes and the
  // admin hasn't just clicked Change.
  useEffect(() => {
    const newId = initialSelected?.id || null;
    if (newId && newId !== lastInitialIdRef.current) {
      // Parent supplied a genuinely new customer identity — reset the sentinel
      // so the new value can hydrate.
      userClearedRef.current = false;
      lastInitialIdRef.current = newId;
      setSelected(initialSelected);
    } else if (initialSelected && !userClearedRef.current && !selected) {
      // First hydration on mount.
      setSelected(initialSelected);
    }
    // If admin just cleared, ignore stale re-hydration from the parent.
  }, [initialSelected]);

  useEffect(() => {
    if (selected) return;
    const query = q.trim();
    if (!query) { setRows([]); setSearchErr(""); setSearching(false); return; }
    let cancelled = false;
    setSearching(true); setSearchErr("");
    const t = setTimeout(async () => {
      try {
        // Reuses the admin customer-search endpoint.
        const r = await axios.get(`${API}/admin/customers?q=${encodeURIComponent(query)}&limit=10`, { headers: auth() });
        if (cancelled) return;
        setRows(r.data?.customers || []);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          setSearchErr("Session expired — please log in again.");
        } else {
          const d = err?.response?.data?.detail;
          setSearchErr(typeof d === "string" ? d : (d?.message || "Customer search failed. Please try again."));
        }
        setRows([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, selected]);

  if (selected) {
    return (
      <div className="border border-[#9A7B4F]/40 bg-[#FAF3E0]/40 p-3 flex items-start justify-between" data-testid="mbg-customer-selected">
        <div>
          <div className="text-[12px] font-medium text-[#282828]">{selected.name}</div>
          <div className="text-[10px] text-[#676664]">{selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</div>
        </div>
        <button type="button" onClick={() => {
          userClearedRef.current = true;
          setSelected(null); onSelect(null); setQ(""); setRows([]); setSearchErr("");
        }} className="text-[10px] text-[#9A7B4F] hover:underline" data-testid="mbg-customer-change">Change</button>
      </div>
    );
  }

  const trimmed = q.trim();
  const showEmpty = !!trimmed && !searching && !searchErr && rows.length === 0;

  return (
    <div className="space-y-2">
      {!showNew && (
        <>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#676664]/50" />
            <input type="text" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search customer by name, email, phone…"
              className="w-full border border-[#282828]/15 pl-8 pr-3 py-2 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
              data-testid="mbg-customer-search" />
            {searching && (
              <Loader2 className="w-3.5 h-3.5 absolute right-2.5 top-2.5 text-[#9A7B4F] animate-spin" data-testid="mbg-customer-searching" />
            )}
          </div>
          {searchErr && (
            <div className="text-[11px] text-red-600 border border-red-200 bg-red-50/60 px-2 py-1.5 flex items-start gap-1" data-testid="mbg-customer-error">
              <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" /> {searchErr}
            </div>
          )}
          {rows.length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-[#282828]/8" data-testid="mbg-customer-results">
              {rows.map(c => {
                const cid = c.customer_id || c.id;
                return (
                  <button key={cid} type="button" onClick={() => { setSelected(c); onSelect({ existing: { ...c, id: cid } }); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#FAF8F0] border-b border-[#282828]/6 last:border-0"
                    data-testid={`mbg-customer-option-${cid}`}>
                    <div className="text-[12px] font-medium">{c.name}</div>
                    <div className="text-[10px] text-[#676664]/70">{c.email || "—"}{c.phone ? ` · ${c.phone}` : ""}</div>
                  </button>
                );
              })}
            </div>
          )}
          {showEmpty && (
            <div className="text-[11px] text-[#676664]/70 border border-[#282828]/8 bg-[#FAF8F0]/40 px-3 py-2" data-testid="mbg-customer-empty">
              No customers found for &quot;{trimmed}&quot;.
            </div>
          )}
          <button type="button" onClick={() => setShowNew(true)}
            className="text-[10px] text-[#9A7B4F] hover:underline" data-testid="mbg-customer-new">
            + Add a new customer
          </button>
        </>
      )}
      {showNew && (
        <div className="space-y-2 border border-[#282828]/12 p-3" data-testid="mbg-customer-new-form">
          <input type="text" placeholder="Name" value={newC.name} onChange={e => setNewC({ ...newC, name: e.target.value })}
            className="w-full border border-[#282828]/15 px-3 py-1.5 text-[12px]" data-testid="mbg-new-name" />
          <input type="email" placeholder="Email" value={newC.email} onChange={e => setNewC({ ...newC, email: e.target.value })}
            className="w-full border border-[#282828]/15 px-3 py-1.5 text-[12px]" data-testid="mbg-new-email" />
          <input type="tel" placeholder="Phone" value={newC.phone} onChange={e => setNewC({ ...newC, phone: e.target.value })}
            className="w-full border border-[#282828]/15 px-3 py-1.5 text-[12px]" data-testid="mbg-new-phone" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => {
              if (!newC.name || !newC.email) { toast.error("Name and email required"); return; }
              onSelect({ new_customer: newC });
              setSelected({ id: "__new__", name: newC.name, email: newC.email, phone: newC.phone });
            }} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-7" data-testid="mbg-new-save">Use</Button>
            <Button size="sm" variant="outline" onClick={() => setShowNew(false)} className="text-[11px] h-7">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── outfit picker (Section P-7b — thumbnail grid) ─────────────────────────

function _outfitThumb(outfit, variation) {
  if (variation && (variation.image || variation.thumbnail)) {
    return variation.image || variation.thumbnail;
  }
  const imgs = outfit?.images || [];
  if (imgs.length) {
    const first = imgs[0];
    return typeof first === "string" ? first : (first.url || first.thumbnail || "");
  }
  return "";
}

function _outfitPrice(source) {
  const p4 = Number(source?.price_4_day || 0);
  const p8 = Number(source?.price_8_day || 0);
  const pd = Number(source?.price_per_day || 0);
  return p4 || p8 || pd || 0;
}

function OutfitPicker({ onPick, existingKeys }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null); // outfit id whose variations are being picked

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/outfits?limit=100`, { headers: auth() });
        if (!cancelled) setRows(Array.isArray(r.data) ? r.data : (r.data.outfits || []));
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return (rows || [])
      .filter(o => !o.archived)
      .filter(o => !qq || (o.name || "").toLowerCase().includes(qq) || (o.category || "").toLowerCase().includes(qq))
      .slice(0, 60);
  }, [rows, q]);

  const expanded = useMemo(
    () => (expandedId ? filtered.find(o => o.id === expandedId) : null),
    [expandedId, filtered],
  );

  const handleCardClick = (o) => {
    const opts = o.rental_options || [];
    if (opts.length === 0) {
      // No variations — add directly.
      onPick({ outfit_id: o.id, rental_option_id: "", outfit_name: o.name });
      return;
    }
    setExpandedId(o.id);
  };

  return (
    <div className="border border-[#282828]/12 p-3 mt-2 bg-white" data-testid="mbg-outfit-picker">
      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#676664]/50" />
        <input type="text" value={q} onChange={e => { setQ(e.target.value); setExpandedId(null); }}
          placeholder="Search outfits by name or category…"
          className="w-full border border-[#282828]/15 pl-8 pr-3 py-2 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
          data-testid="mbg-outfit-search" />
      </div>

      {loading && (
        <div className="text-[11px] text-[#676664]/60 flex items-center gap-1 py-6 justify-center">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading products…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-[11px] text-[#676664]/60 py-6 text-center">No products match your search.</div>
      )}

      {!loading && !expanded && filtered.length > 0 && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[420px] overflow-y-auto pr-1"
          data-testid="mbg-outfit-grid"
        >
          {filtered.map(o => {
            const opts = o.rental_options || [];
            const noVarKey = `${o.id}::`;
            const allVarsUsed = opts.length > 0 && opts.every(v => existingKeys.has(`${o.id}::${v.id}`));
            const singleUsed = opts.length === 0 && existingKeys.has(noVarKey);
            const disabled = singleUsed || allVarsUsed;
            const thumb = _outfitThumb(o);
            const price = _outfitPrice(o);
            return (
              <button
                key={o.id}
                type="button"
                disabled={disabled}
                onClick={() => handleCardClick(o)}
                className={`group relative border overflow-hidden text-left flex flex-col transition-all ${
                  disabled
                    ? "border-[#282828]/8 opacity-40 cursor-not-allowed"
                    : "border-[#282828]/12 hover:border-[#9A7B4F] hover:shadow-sm"
                }`}
                data-testid={`mbg-outfit-card-${o.id}`}
              >
                <div className="relative w-full aspect-[3/4] bg-[#FAF8F0] overflow-hidden">
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={o.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[9px] tracking-[0.25em] text-[#B8987B]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      MAHRA
                    </div>
                  )}
                  {opts.length > 1 && (
                    <span className="absolute top-1 right-1 bg-white/95 text-[8px] uppercase tracking-wider text-[#676664] px-1.5 py-0.5 border border-[#282828]/8">
                      {opts.length} variations
                    </span>
                  )}
                  {disabled && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                      <span className="text-[9px] uppercase tracking-wider text-[#282828] bg-white px-2 py-1 border border-[#282828]/20">
                        Already added
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-[11px] text-[#282828] font-medium leading-tight line-clamp-2" data-testid={`mbg-outfit-card-name-${o.id}`}>
                    {o.name}
                  </div>
                  {price > 0 && (
                    <div className="text-[10px] text-[#9A7B4F] mt-1" data-testid={`mbg-outfit-card-price-${o.id}`}>
                      from ${price.toFixed(0)}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!loading && expanded && (
        <div className="border border-[#9A7B4F]/30 bg-[#FAF3E0]/25 p-3" data-testid={`mbg-variation-panel-${expanded.id}`}>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-16 h-20 bg-white flex-shrink-0 overflow-hidden border border-[#282828]/8">
              {_outfitThumb(expanded) ? (
                <img src={_outfitThumb(expanded)} alt={expanded.name} className="w-full h-full object-cover" />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium text-[#282828]">{expanded.name}</div>
              <div className="text-[10px] text-[#676664]/70">Select a variation to add</div>
            </div>
            <button
              type="button"
              onClick={() => setExpandedId(null)}
              className="text-[10px] text-[#9A7B4F] hover:underline"
              data-testid="mbg-variation-back"
            >
              ← Back
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(expanded.rental_options || []).map(v => {
              const already = existingKeys.has(`${expanded.id}::${v.id}`);
              const vp = _outfitPrice(v) || _outfitPrice(expanded);
              const vthumb = _outfitThumb(expanded, v);
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={already}
                  onClick={() => onPick({ outfit_id: expanded.id, rental_option_id: v.id, outfit_name: expanded.name, variation_name: v.name })}
                  className={`border overflow-hidden text-left flex flex-col ${
                    already ? "opacity-40 cursor-not-allowed border-[#282828]/8" : "border-[#282828]/12 hover:border-[#9A7B4F]"
                  }`}
                  data-testid={`mbg-variation-${expanded.id}-${v.id}`}
                >
                  <div className="w-full aspect-[3/4] bg-white overflow-hidden">
                    {vthumb ? (
                      <img src={vthumb} alt={v.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[8px] tracking-[0.2em] text-[#B8987B]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        MAHRA
                      </div>
                    )}
                  </div>
                  <div className="p-1.5">
                    <div className="text-[10px] text-[#282828] font-medium leading-tight line-clamp-2">{v.name}</div>
                    {vp > 0 && (
                      <div className="text-[9px] text-[#9A7B4F] mt-0.5">from ${vp.toFixed(0)}</div>
                    )}
                    {already && <div className="text-[8px] text-[#676664]/60 mt-0.5">· already added</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── main modal ─────────────────────────────────────────────────────────────

export function AdminManualBookingGroupModal({ onClose, onCreated, existingGroupId = null, peerBookingIds = [] }) {
  const isEdit = !!existingGroupId;
  const [loadingGroup, setLoadingGroup] = useState(isEdit);
  const [groupLoadErr, setGroupLoadErr] = useState("");
  const [originalGroup, setOriginalGroup] = useState(null); // snapshot at load time — for audit diff display
  const [editReason, setEditReason] = useState("");
  const [notesAdmin, setNotesAdmin] = useState("");
  const [internalPaymentNotes, setInternalPaymentNotes] = useState("");
  const [customerSel, setCustomerSel] = useState(null); // {existing:{}} or {new_customer:{}}
  const [eventDate, setEventDate] = useState("");
  const [rentalDays, setRentalDays] = useState(4);
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [deliveryFee, setDeliveryFee] = useState(0);

  // v6.1 — enforce Delivery⇄Pickup fee invariant on every method change.
  // Delivery→Pickup zeroes the fee; Pickup→Delivery restores the previous
  // value or seeds the configured default. Backend re-applies the same
  // invariant in _compute_group_pricing (belt + suspenders).
  useEffect(() => {
    setDeliveryFee(prev => nextDeliveryFeeOnMethodChange(deliveryMethod, prev));
  }, [deliveryMethod]);
  const [items, setItems] = useState([]); // {outfit_id, rental_option_id, outfit_name, variation_name, rent_override_amount, bond_override_amount, override_reason}
  const [showPicker, setShowPicker] = useState(false);
  const [discountMode, setDiscountMode] = useState("none"); // none | percent | amount
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [paymentStatusManual, setPaymentStatusManual] = useState("unpaid");
  // Backend still accepts an optional trial_lead_id and hydrates it from an
  // existing group in edit mode so historical / fitting→booking conversion
  // linkage keeps working — but we deliberately do NOT expose a raw ID input
  // in the creation UI (admins shouldn't be typing internal DB ids).
  const [trialLeadId, setTrialLeadId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // ── Phase 5D — load the group when in edit mode ────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const params = peerBookingIds.length ? `?peer_booking_ids=${encodeURIComponent(peerBookingIds.join(","))}` : "";
        const r = await axios.get(`${API}/admin/bookings/group/${existingGroupId}${params}`, { headers: auth() });
        if (cancelled) return;
        const g = r.data.group;
        setOriginalGroup(g);
        setCustomerSel({ existing: { id: g.customer_id, name: g.customer_name, email: g.customer_email, phone: "" } });
        setEventDate(g.event_date || "");
        setRentalDays(g.rental_days || 4);
        setDeliveryMethod(g.delivery_method || "pickup");
        setDeliveryFee(g.delivery_fee || 0);
        setNotesAdmin(g.notes_admin || "");
        setInternalPaymentNotes(g.internal_payment_notes || "");
        if (g.admin_discount_percent && Number(g.admin_discount_percent) > 0) {
          setDiscountMode("percent"); setDiscountValue(String(g.admin_discount_percent));
        } else if (g.admin_discount_amount && Number(g.admin_discount_amount) > 0) {
          setDiscountMode("amount"); setDiscountValue(String(g.admin_discount_amount));
        }
        setDiscountReason(g.admin_discount_reason || "");
        setPaymentStatusManual(g.payment_status_manual || "unpaid");
        if (g.trial_lead_id) setTrialLeadId(g.trial_lead_id);
        setItems((g.items_at_creation || []).map(it => ({
          outfit_id: it.outfit_id,
          rental_option_id: it.rental_option_id || "",
          outfit_name: it.outfit_name || "",
          variation_name: it.rental_option_name || "",
          rent_override_amount: it.rent_override_amount == null ? "" : String(it.rent_override_amount),
          bond_override_amount: it.bond_override_amount == null ? "" : String(it.bond_override_amount),
          override_reason: it.override_reason || "",
        })));
      } catch (err) {
        if (cancelled) return;
        const d = err?.response?.data?.detail;
        setGroupLoadErr(typeof d === "string" ? d : (d?.message || "Failed to load booking group"));
      } finally { if (!cancelled) setLoadingGroup(false); }
    })();
    return () => { cancelled = true; };
  }, [isEdit, existingGroupId]);

  const existingKeys = useMemo(() => new Set(items.map(i => `${i.outfit_id}::${i.rental_option_id || ""}`)), [items]);

  // ── Phase 5C: override validity + audit helpers ────────────────────────────
  const hasRentOverride  = (it) => it.rent_override_amount !== "" && it.rent_override_amount != null;
  const hasBondOverride  = (it) => it.bond_override_amount !== "" && it.bond_override_amount != null;
  const itemHasOverride  = (it) => hasRentOverride(it) || hasBondOverride(it);
  const itemReasonMissing = (it) => itemHasOverride(it) && !(it.override_reason || "").trim();

  const overriddenCount = useMemo(() => items.filter(itemHasOverride).length, [items]);
  const itemsMissingReason = useMemo(() => items.reduce((acc, it, idx) => (itemReasonMissing(it) ? [...acc, idx] : acc), []), [items]);

  const discountActive = discountMode !== "none" && discountValue !== "" && Number(discountValue) > 0;
  const discountReasonMissing = discountActive && !(discountReason || "").trim();

  const buildBody = useCallback((forCreate = false) => {
    const body = {
      event_date: eventDate,
      rental_days: Number(rentalDays) || 4,
      delivery_method: deliveryMethod,
      delivery_fee: Number(deliveryFee) || 0,
      admin_discount_reason: discountReason,
      notes_admin: notesAdmin || "",
      internal_payment_notes: internalPaymentNotes || "",
      items: items.map(i => ({
        outfit_id: i.outfit_id,
        rental_option_id: i.rental_option_id || "",
        quantity: 1,
        rent_override_amount: i.rent_override_amount === "" || i.rent_override_amount == null ? null : Number(i.rent_override_amount),
        bond_override_amount: i.bond_override_amount === "" || i.bond_override_amount == null ? null : Number(i.bond_override_amount),
        override_reason: i.override_reason || "",
      })),
    };
    if (discountMode === "percent" && discountValue) body.admin_discount_percent = Number(discountValue);
    if (discountMode === "amount" && discountValue) body.admin_discount_amount = Number(discountValue);
    if (customerSel?.existing) body.customer_id = customerSel.existing.id;
    if (customerSel?.new_customer) body.new_customer = customerSel.new_customer;
    if (trialLeadId.trim()) body.trial_lead_id = trialLeadId.trim();
    if (forCreate || isEdit) body.payment_status_manual = paymentStatusManual;
    if (isEdit) body.edit_reason = editReason || "";
    if (isEdit && peerBookingIds.length) body.peer_booking_ids = peerBookingIds;
    return body;
  }, [eventDate, rentalDays, deliveryMethod, deliveryFee, items, discountMode, discountValue, discountReason, customerSel, notesAdmin, internalPaymentNotes, paymentStatusManual, isEdit, editReason, peerBookingIds, trialLeadId]);

  // ── Dedicated preview payload ────────────────────────────────────────
  // ONLY the fields the backend needs for pricing + availability.  We do NOT
  // include notes / payment_status_manual / trial_lead_id / customer object —
  // those don't affect the preview response, and using them would retrigger
  // the preview loop on unrelated keystrokes.
  const previewBody = useMemo(() => {
    const body = {
      event_date: eventDate,
      rental_days: Number(rentalDays) || 4,
      delivery_method: deliveryMethod,
      delivery_fee: Number(deliveryFee) || 0,
      admin_discount_reason: discountReason,
      items: items.map(i => ({
        outfit_id: i.outfit_id,
        rental_option_id: i.rental_option_id || "",
        quantity: 1,
        rent_override_amount:
          i.rent_override_amount === "" || i.rent_override_amount == null
            ? null : Number(i.rent_override_amount),
        bond_override_amount:
          i.bond_override_amount === "" || i.bond_override_amount == null
            ? null : Number(i.bond_override_amount),
        override_reason: i.override_reason || "",
      })),
    };
    if (discountMode === "percent" && discountValue) body.admin_discount_percent = Number(discountValue);
    if (discountMode === "amount" && discountValue)  body.admin_discount_amount  = Number(discountValue);
    if (isEdit && peerBookingIds.length) body.peer_booking_ids = peerBookingIds;
    return body;
  }, [eventDate, rentalDays, deliveryMethod, deliveryFee, items, discountMode, discountValue, discountReason, isEdit, peerBookingIds]);

  // Stable hash of the preview payload — used for dedupe.
  const previewKey = useMemo(() => JSON.stringify(previewBody), [previewBody]);

  const runPreviewSeqRef = useRef(0);
  const abortRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastAppliedKeyRef = useRef("");

  const runPreview = useCallback(async () => {
    // Guard: don't fire (and don't clear a previously-loaded preview) unless
    // the minimum inputs are present.
    if (!eventDate || items.length === 0) {
      setPreview(null); setPreviewing(false); setPreviewErr("");
      return;
    }
    // Dedupe: skip if the exact same payload already produced a result.
    if (previewKey === lastAppliedKeyRef.current) {
      setPreviewing(false);
      return;
    }
    // Cancel any in-flight request + its 10s timeout.
    if (abortRef.current) { try { abortRef.current.abort(); } catch (_) {} }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }

    const seq = ++runPreviewSeqRef.current;
    const ac = new AbortController();
    abortRef.current = ac;
    // Hard 10-second cap — never leave the spinner up longer than this.
    timeoutRef.current = setTimeout(() => {
      try { ac.abort(); } catch (_) {}
    }, 10000);

    setPreviewing(true); setPreviewErr("");
    try {
      const url = isEdit
        ? `${API}/admin/bookings/group/${existingGroupId}/edit/preview`
        : `${API}/admin/bookings/manual/preview`;
      const r = await axios.post(url, previewBody, { headers: auth(), signal: ac.signal });
      if (seq !== runPreviewSeqRef.current) return; // stale — a newer call has been fired
      setPreview(r.data);
      setPreviewErr("");
      lastAppliedKeyRef.current = previewKey;   // remember what produced this result
    } catch (err) {
      if (seq !== runPreviewSeqRef.current) return; // stale
      // Distinguish user-triggered cancellation from a real failure.
      const isCancel = axios.isCancel?.(err) || err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED";
      if (isCancel) {
        // If the cancellation was our 10-s timeout AND no in-flight successor exists,
        // surface a friendly error.  Otherwise stay silent — a newer call will
        // update state shortly.
        if (seq === runPreviewSeqRef.current) {
          setPreviewErr("Preview timed out — please try again.");
          setPreview(null);
        }
      } else {
        const d = err?.response?.data?.detail;
        setPreviewErr(typeof d === "string" ? d : (d?.message || "Preview failed"));
        setPreview(null);
      }
    } finally {
      // The latest call is responsible for clearing its own spinner + timer.
      if (seq === runPreviewSeqRef.current) {
        setPreviewing(false);
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      }
    }
  }, [previewBody, previewKey, eventDate, items.length, isEdit, existingGroupId]);

  // Debounced auto-preview on any pricing/availability change (500ms).
  // Dependency is `previewKey` (a string), NOT the callback reference —
  // this stops re-triggers from unrelated state churn (notes, customer
  // object identity changes, trial-lead field, etc).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { runPreview(); }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // Cleanup on unmount — kill any in-flight request/timer so we don't leak.
  useEffect(() => {
    return () => {
      if (abortRef.current) { try { abortRef.current.abort(); } catch (_) {} }
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const anyConflict = preview?.any_conflict;
  const canSubmit = (
    !!eventDate && items.length > 0 && !!customerSel && preview && !anyConflict && !previewing && !submitting
    && itemsMissingReason.length === 0 && !discountReasonMissing
  );

  const doCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEdit) {
        const r = await axios.post(`${API}/admin/bookings/group/${existingGroupId}/edit`, buildBody(true), { headers: auth() });
        const nAdd = (r.data.booking_ids_added || []).length;
        const nRem = (r.data.booking_ids_removed || []).length;
        const nUpd = (r.data.booking_ids_updated || []).length;
        toast.success(`Group ${r.data.group_id} updated · ${nUpd} updated · ${nAdd} added · ${nRem} removed`);
        onCreated?.(r.data);
        onClose?.();
      } else {
        const r = await axios.post(`${API}/admin/bookings/manual/create`, buildBody(true), { headers: auth() });
        toast.success(`Booking group ${r.data.group_id} created (${r.data.booking_ids.length} item${r.data.booking_ids.length === 1 ? "" : "s"})`);
        onCreated?.(r.data);
        onClose?.();
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || (isEdit ? "Edit failed" : "Create failed")));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose} data-testid="mbg-modal">
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-5xl my-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#282828]/10">
          <div>
            <h3 className="text-[15px] text-[#282828]">
              {isEdit
                ? (originalGroup && originalGroup._synthetic
                    ? <>Edit Booking{originalGroup._kind === "cart_group_peers" ? " Group" : ""} · <span className="text-[#9A7B4F]" data-testid="mbg-edit-group-id">{existingGroupId}</span> <span className="text-[10px] text-[#676664]/60 uppercase tracking-wider">(legacy)</span></>
                    : <>Edit Booking Group · <span className="text-[#9A7B4F]" data-testid="mbg-edit-group-id">{existingGroupId}</span></>)
                : "Manual Booking · Multi-Product"}
            </h3>
            <p className="text-[11px] text-[#676664]/60">
              {isEdit
                ? (originalGroup?._synthetic
                    ? "Phase 5D Rev 2 — legacy booking edit · will be promoted to an audited group on save · IDs preserved"
                    : "Phase 5D — edit an existing manual booking group · availability re-checked · full audit trail")
                : "Phase 5C — pricing, bond & discount overrides with live audit"}
            </p>
          </div>
          <button onClick={onClose} className="text-[#676664]/40 hover:text-[#282828] p-1" data-testid="mbg-close"><X className="w-4 h-4" /></button>
        </div>

        {loadingGroup && (
          <div className="p-8 flex items-center justify-center text-[12px] text-[#676664]" data-testid="mbg-loading-group">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading booking group…
          </div>
        )}
        {groupLoadErr && !loadingGroup && (
          <div className="p-4 mx-5 my-4 border border-red-200 bg-red-50 text-red-700 text-[12px] flex items-start gap-2" data-testid="mbg-group-load-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {groupLoadErr}
          </div>
        )}

        {!loadingGroup && !groupLoadErr && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-5">
          {/* LEFT: form */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Customer</label>
              <div className="mt-1"><CustomerSearchBox
                onSelect={setCustomerSel}
                initialSelected={isEdit && originalGroup ? { id: originalGroup.customer_id, name: originalGroup.customer_name, email: originalGroup.customer_email, phone: "" } : null}
              /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Event date</span>
                <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
                  className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
                  data-testid="mbg-event-date" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Rental days</span>
                <input type="number" min="1" max="30" value={rentalDays} onChange={e => setRentalDays(e.target.value)}
                  className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
                  data-testid="mbg-rental-days" />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Delivery method</span>
                <select value={deliveryMethod} onChange={e => setDeliveryMethod(e.target.value)}
                  className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px] bg-white"
                  data-testid="mbg-delivery-method">
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Delivery</option>
                </select>
              </label>
              {deliveryMethod === "delivery" && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Delivery fee ($)</span>
                  <input type="number" min="0" step="0.01" value={deliveryFee} onChange={e => setDeliveryFee(e.target.value)}
                    className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px]" data-testid="mbg-delivery-fee" />
                </label>
              )}
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Items ({items.length})</span>
                  {overriddenCount > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 border border-[#9A7B4F]/40 text-[#9A7B4F] bg-[#FAF3E0]/60 uppercase tracking-wider" data-testid="mbg-override-badge-count">
                      {overriddenCount} overridden
                    </span>
                  )}
                  {preview?.pricing?.bundle_discount_percent > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 border border-emerald-400/40 text-emerald-700 bg-emerald-50 uppercase tracking-wider" data-testid="mbg-bundle-badge">
                      Auto bundle {preview.pricing.bundle_discount_percent}%
                    </span>
                  )}
                </div>
                <Button size="sm" onClick={() => setShowPicker(v => !v)}
                  className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] h-7 px-3" data-testid="mbg-add-item">
                  <Plus className="w-3 h-3 mr-1" /> Add product
                </Button>
              </div>
              {showPicker && (
                <OutfitPicker
                  existingKeys={existingKeys}
                  onPick={(p) => {
                    setItems([...items, { ...p, rent_override_amount: "", bond_override_amount: "", override_reason: "" }]);
                    setShowPicker(false);
                  }}
                />
              )}
              {items.length === 0 && !showPicker && (
                <div className="border border-dashed border-[#282828]/12 p-6 text-center mt-2 text-[11px] text-[#676664]/60">
                  <Package className="w-6 h-6 mx-auto text-[#676664]/25 mb-2" />
                  No items yet. Click <strong>Add product</strong> to pick one.
                </div>
              )}
              <div className="space-y-2 mt-2">
                {items.map((it, idx) => {
                  const av = preview?.availability?.find(a => a.line_index === idx);
                  const pi = preview?.items?.find(a => a.line_index === idx);
                  const conflicting = av && !av.available;
                  const rentOverridden = hasRentOverride(it);
                  const bondOverridden = hasBondOverride(it);
                  const overridden = rentOverridden || bondOverridden;
                  const reasonMissing = itemReasonMissing(it);
                  const rentDelta = (pi && rentOverridden) ? +(Number(it.rent_override_amount) - Number(pi.base_rent)).toFixed(2) : 0;
                  const bondDelta = (pi && bondOverridden) ? +(Number(it.bond_override_amount) - Number(pi.base_bond)).toFixed(2) : 0;
                  return (
                    <div key={idx}
                      className={`border p-3 ${conflicting ? "border-red-300 bg-red-50/40" : overridden ? "border-[#9A7B4F]/40 bg-[#FAF3E0]/30" : "border-[#282828]/10"}`}
                      data-testid={`mbg-item-row-${idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-[12px] font-medium text-[#282828]">{it.outfit_name}{it.variation_name ? ` · ${it.variation_name}` : ""}</div>
                            {overridden && (
                              <span className="text-[9px] px-1.5 py-0.5 border border-[#9A7B4F]/40 text-[#9A7B4F] bg-white uppercase tracking-wider" data-testid={`mbg-item-overridden-badge-${idx}`}>
                                Overridden
                              </span>
                            )}
                          </div>
                          {pi && (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-[10px]">
                              <div className="flex items-center gap-1.5" data-testid={`mbg-item-rent-audit-${idx}`}>
                                <span className="text-[#676664]/70">Rent:</span>
                                {rentOverridden ? (
                                  <>
                                    <span className="line-through text-[#676664]/50">${pi.base_rent.toFixed(2)}</span>
                                    <span className="font-medium text-[#282828]">→ ${Number(it.rent_override_amount).toFixed(2)}</span>
                                    <span className={rentDelta > 0 ? "text-red-600" : "text-emerald-700"}>
                                      ({rentDelta > 0 ? "+" : "−"}${Math.abs(rentDelta).toFixed(2)})
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-[#282828]">${pi.base_rent.toFixed(2)}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5" data-testid={`mbg-item-bond-audit-${idx}`}>
                                <span className="text-[#676664]/70">Bond:</span>
                                {bondOverridden ? (
                                  <>
                                    <span className="line-through text-[#676664]/50">${pi.base_bond.toFixed(2)}</span>
                                    <span className="font-medium text-[#282828]">→ ${Number(it.bond_override_amount).toFixed(2)}</span>
                                    <span className={bondDelta > 0 ? "text-red-600" : "text-emerald-700"}>
                                      ({bondDelta > 0 ? "+" : "−"}${Math.abs(bondDelta).toFixed(2)})
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-[#282828]">${pi.base_bond.toFixed(2)}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {conflicting && (
                            <div className="text-[10px] text-red-600 flex items-center gap-1 mt-1"><AlertCircle className="w-3 h-3" /> Not available on this date (conflicts with {av.conflict_booking_id})</div>
                          )}
                          {av?.available && (
                            <div className="text-[10px] text-emerald-700 flex items-center gap-1 mt-1"><CheckCircle2 className="w-3 h-3" /> Available</div>
                          )}
                          <div className="grid grid-cols-[1fr_1fr_1.5fr_auto] gap-2 mt-2 items-center">
                            <div className="relative">
                              <input type="number" min="0" step="0.01" placeholder="Rent override $" value={it.rent_override_amount}
                                onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, rent_override_amount: e.target.value } : x))}
                                className={`w-full border px-2 py-1 text-[11px] ${rentOverridden ? "border-[#9A7B4F] bg-white" : "border-[#282828]/15"}`}
                                data-testid={`mbg-item-rent-${idx}`} />
                            </div>
                            <div className="relative">
                              <input type="number" min="0" step="0.01" placeholder="Bond override $" value={it.bond_override_amount}
                                onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, bond_override_amount: e.target.value } : x))}
                                className={`w-full border px-2 py-1 text-[11px] ${bondOverridden ? "border-[#9A7B4F] bg-white" : "border-[#282828]/15"}`}
                                data-testid={`mbg-item-bond-${idx}`} />
                            </div>
                            <input type="text"
                              placeholder={overridden ? "Reason (required) *" : "Override reason"}
                              value={it.override_reason}
                              onChange={e => setItems(items.map((x, i) => i === idx ? { ...x, override_reason: e.target.value } : x))}
                              className={`border px-2 py-1 text-[11px] ${reasonMissing ? "border-red-400 bg-red-50/40" : "border-[#282828]/15"}`}
                              data-testid={`mbg-item-reason-${idx}`} />
                            {overridden && (
                              <button type="button"
                                onClick={() => setItems(items.map((x, i) => i === idx ? { ...x, rent_override_amount: "", bond_override_amount: "", override_reason: "" } : x))}
                                className="text-[#9A7B4F] hover:text-[#8B6B3F] p-1"
                                title="Reset overrides"
                                data-testid={`mbg-item-reset-${idx}`}>
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {reasonMissing && (
                            <div className="text-[10px] text-red-600 flex items-center gap-1 mt-1" data-testid={`mbg-item-reason-required-${idx}`}>
                              <AlertCircle className="w-3 h-3" /> Reason is required when a rent or bond override is set.
                            </div>
                          )}
                        </div>
                        <button onClick={() => setItems(items.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700 p-1" data-testid={`mbg-remove-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Phase 5F — read-only history of cancelled children ─────── */}
              {isEdit && Array.isArray(originalGroup?.cancelled_items_at_creation) && originalGroup.cancelled_items_at_creation.length > 0 && (
                <div className="mt-4 border border-[#282828]/12 bg-[#F5F1EA]/40" data-testid="mbg-cancelled-history">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-[#282828]/8">
                    <RotateCcw className="w-3.5 h-3.5 text-[#676664]" />
                    <span className="text-[10px] uppercase tracking-wider text-[#676664] font-medium">Cancelled items — read-only history</span>
                    <span className="text-[9px] px-1.5 py-0.5 border border-[#676664]/40 text-[#676664] bg-white uppercase tracking-wider">
                      {originalGroup.cancelled_items_at_creation.length}
                    </span>
                  </div>
                  <div className="divide-y divide-[#282828]/6">
                    {originalGroup.cancelled_items_at_creation.map((ci, i) => (
                      <div key={i} className="px-3 py-2 flex items-center justify-between gap-2 text-[11px]"
                        data-testid={`mbg-cancelled-item-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] text-[#282828]">
                            {ci.outfit_name}{ci.rental_option_name ? ` · ${ci.rental_option_name}` : ""}
                          </div>
                          <div className="text-[10px] text-[#676664]/70">
                            {ci.booking_id ? `${ci.booking_id} · ` : ""}
                            cancelled — not included in current pricing
                          </div>
                        </div>
                        <div className="text-right text-[11px] text-[#676664]">
                          <div>rent ${Number(ci.applied_rent || 0).toFixed(2)}</div>
                          <div>bond ${Number(ci.applied_bond || 0).toFixed(2)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-2 text-[10px] text-[#676664]/70 border-t border-[#282828]/8 bg-white/60">
                    These items were cancelled previously. They stay in the booking history but do not affect current rent, bond, availability or analytics.
                  </div>
                </div>
              )}
            </div>

            {/* Admin discount */}
            <div className="border-t border-[#282828]/8 pt-4">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Admin discount (optional)</span>
                {discountActive && (
                  <span className="text-[9px] px-1.5 py-0.5 border border-[#9A7B4F]/40 text-[#9A7B4F] bg-[#FAF3E0]/60 uppercase tracking-wider" data-testid="mbg-discount-active-badge">Active</span>
                )}
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2 items-end">
                <select value={discountMode} onChange={e => { setDiscountMode(e.target.value); setDiscountValue(""); }}
                  className="border border-[#282828]/15 px-2 py-1.5 text-[11px] bg-white" data-testid="mbg-discount-mode">
                  <option value="none">None</option>
                  <option value="percent">Percent</option>
                  <option value="amount">Fixed $</option>
                </select>
                <input type="number" min="0" step="0.01" placeholder={discountMode === "percent" ? "%" : "$"} value={discountValue} disabled={discountMode === "none"}
                  onChange={e => setDiscountValue(e.target.value)}
                  className="border border-[#282828]/15 px-2 py-1.5 text-[11px]" data-testid="mbg-discount-value" />
                <input type="text"
                  placeholder={discountActive ? "Reason (required) *" : "Reason (optional)"}
                  value={discountReason} disabled={discountMode === "none"}
                  onChange={e => setDiscountReason(e.target.value)}
                  className={`border px-2 py-1.5 text-[11px] col-span-2 ${discountReasonMissing ? "border-red-400 bg-red-50/40" : "border-[#282828]/15"}`}
                  data-testid="mbg-discount-reason" />
              </div>
              {discountReasonMissing && (
                <div className="text-[10px] text-red-600 flex items-center gap-1 mt-1" data-testid="mbg-discount-reason-required">
                  <AlertCircle className="w-3 h-3" /> Reason is required when an admin discount is applied.
                </div>
              )}
              <p className="text-[10px] text-[#676664]/60 mt-1">Percent and fixed cannot be used at the same time. Applied AFTER the automatic bundle discount. Bond is never discounted.</p>
            </div>

            {/* Booking notes + payment status (visible always; payment editable in edit mode) */}
            <div className="border-t border-[#282828]/8 pt-4 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Booking notes (admin)</span>
                <textarea rows={2} value={notesAdmin} onChange={e => setNotesAdmin(e.target.value)}
                  className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
                  placeholder="Internal notes about this booking group" data-testid="mbg-notes-admin" />
              </label>
              {/* Payment status — available in both create and edit modes.
                  Stripe-triggered statuses (stripe_link_sent / stripe_charged)
                  are deliberately NOT exposed as selectable options here;
                  those are set as a consequence of the actual Stripe action
                  succeeding after creation (from the booking detail view).
                  If an existing group already carries a Stripe status we show
                  it read-only so admins can see it without being able to
                  overwrite a real Stripe event with a manual dropdown. */}
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Payment status</span>
                {(paymentStatusManual === "stripe_link_sent" || paymentStatusManual === "stripe_charged") ? (
                  <div className="mt-1 border border-[#9A7B4F]/30 bg-[#FAF3E0]/40 px-3 py-1.5 text-[12px] text-[#3D2B1F] flex items-center justify-between" data-testid="mbg-payment-status-readonly">
                    <span>
                      {paymentStatusManual === "stripe_link_sent" ? "Stripe link sent" : "Stripe charged (saved card)"}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider text-[#9A7B4F]">read-only</span>
                  </div>
                ) : (
                  <select value={paymentStatusManual} onChange={e => setPaymentStatusManual(e.target.value)}
                    className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px] bg-white" data-testid="mbg-payment-status">
                    <option value="unpaid">Unpaid</option>
                    <option value="paid_manual">Paid manually / bank transfer / cash</option>
                    <option value="partial">Partial</option>
                  </select>
                )}
                <span className="text-[10px] text-[#676664]/60 mt-1 block">This only records the payment status. Payment links and saved-card charges are triggered from the booking detail after creation.</span>
              </label>
              {isEdit && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Edit reason</span>
                  <textarea rows={2} value={editReason} onChange={e => setEditReason(e.target.value)}
                    className="mt-1 w-full border border-[#282828]/15 px-3 py-1.5 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
                    placeholder="Why is this booking being changed? (stored in audit trail)" data-testid="mbg-edit-reason" />
                </label>
              )}
            </div>
          </div>

          {/* RIGHT: live totals */}
          <div className="lg:col-span-1">
            <div className="border border-[#282828]/12 bg-[#FAF8F0]/40 p-4 sticky top-4" data-testid="mbg-totals">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-[12px] font-medium text-[#282828]">Live totals</h4>
                {previewing && <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" />}
              </div>
              {previewErr && <div className="text-[11px] text-red-600 mb-2" data-testid="mbg-totals-error">{previewErr}</div>}
              {!preview && !previewErr && <div className="text-[11px] text-[#676664]/60">Add items + event date to see totals.</div>}
              {preview && (
                <div className="text-[11px] space-y-1.5">
                  <Row label="Subtotal (applied rents)" val={preview.pricing.subtotal_original} />
                  {preview.pricing.bundle_discount_percent > 0 ? (
                    <Row label={`Bundle discount (${preview.pricing.bundle_discount_percent}% · auto)`} val={-preview.pricing.bundle_discount_amount} />
                  ) : (
                    items.length >= 2 && (
                      <div className="text-[10px] text-[#676664]/60 italic" data-testid="mbg-bundle-not-configured">
                        No bundle discount configured.
                      </div>
                    )
                  )}
                  <Row label="After bundle" val={preview.pricing.post_bundle_subtotal} muted />
                  {preview.pricing.admin_discount_amount_applied > 0 && (
                    <Row label={preview.pricing.admin_discount_percent_applied > 0 ? `Admin discount (${preview.pricing.admin_discount_percent_applied}%)` : "Admin discount"}
                         val={-preview.pricing.admin_discount_amount_applied} />
                  )}
                  <Row label="Rental subtotal (final)" val={preview.pricing.post_admin_subtotal} bold />
                  {preview.pricing.delivery_fee > 0 && <Row label="Delivery fee" val={preview.pricing.delivery_fee} />}
                  <Row label="Bond (untouched)" val={preview.pricing.bond_total} />
                  <div className="border-t border-[#282828]/12 pt-2 mt-2">
                    <Row label="Grand total" val={preview.pricing.grand_total} bold big />
                  </div>

                  {/* Phase 5C — audit summary */}
                  {(overriddenCount > 0 || discountActive) && (
                    <div className="mt-3 pt-3 border-t border-[#282828]/10 space-y-1.5" data-testid="mbg-audit-summary">
                      <div className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Audit</div>
                      <div className="text-[10px] text-[#282828]" data-testid="mbg-audit-overrides">
                        {overriddenCount === 0
                          ? "No per-item overrides."
                          : `${overriddenCount} of ${items.length} item${items.length === 1 ? "" : "s"} overridden.`}
                      </div>
                      {discountActive && (
                        <div className="text-[10px] text-[#282828]" data-testid="mbg-audit-discount">
                          Admin discount: {discountMode === "percent" ? `${discountValue}%` : `$${Number(discountValue).toFixed(2)}`}
                          {discountReason ? ` — "${discountReason}"` : ""}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Phase 5D — original vs updated comparison when editing */}
                  {isEdit && originalGroup && preview?.changes && (
                    <div className="mt-3 pt-3 border-t border-[#282828]/10 space-y-1" data-testid="mbg-edit-diff">
                      <div className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Original → Updated</div>
                      {Object.keys(preview.changes.fields || {}).length === 0
                        && (preview.changes.items?.added || []).length === 0
                        && (preview.changes.items?.removed || []).length === 0
                        && (preview.changes.items?.changed || []).length === 0 && (
                        <div className="text-[10px] text-[#676664]/60" data-testid="mbg-edit-no-changes">No changes yet.</div>
                      )}
                      {Object.entries(preview.changes.fields || {}).map(([k, v]) => (
                        <div key={k} className="text-[10px] text-[#282828]" data-testid={`mbg-edit-field-${k}`}>
                          <span className="text-[#676664]/70">{k}:</span> <span className="line-through text-[#676664]/50">{String(v.before ?? "—")}</span> → <span className="font-medium">{String(v.after ?? "—")}</span>
                        </div>
                      ))}
                      {(preview.changes.items?.added || []).length > 0 && (
                        <div className="text-[10px] text-emerald-700" data-testid="mbg-edit-items-added">
                          + Added: {preview.changes.items.added.map(i => i.outfit_name).join(", ")}
                        </div>
                      )}
                      {(preview.changes.items?.removed || []).length > 0 && (
                        <div className="text-[10px] text-red-600" data-testid="mbg-edit-items-removed">
                          − Removed: {preview.changes.items.removed.map(i => i.outfit_name).join(", ")}
                        </div>
                      )}
                      {(preview.changes.items?.changed || []).length > 0 && (
                        <div className="text-[10px] text-[#9A7B4F]" data-testid="mbg-edit-items-changed">
                          Modified: {preview.changes.items.changed.length} item(s)
                        </div>
                      )}
                    </div>
                  )}

                  {anyConflict && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 text-[10px] text-red-700 flex items-start gap-1">
                      <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      One or more items have an availability conflict. Resolve before saving.
                    </div>
                  )}
                </div>
              )}
              <Button disabled={!canSubmit} onClick={doCreate}
                className="w-full mt-4 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-9 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="mbg-submit">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                {submitting
                  ? (isEdit ? "Saving…" : "Creating…")
                  : (isEdit ? "Save changes" : "Create booking group")}
              </Button>
              {!canSubmit && !submitting && (
                <p className="text-[10px] text-[#676664]/60 mt-2 text-center" data-testid="mbg-submit-help">
                  {!customerSel ? "Select a customer."
                    : !eventDate ? "Pick an event date."
                    : items.length === 0 ? "Add at least one item."
                    : anyConflict ? "Fix conflicting items."
                    : itemsMissingReason.length > 0 ? `Add reason for item${itemsMissingReason.length === 1 ? "" : "s"} ${itemsMissingReason.map(i => i + 1).join(", ")}.`
                    : discountReasonMissing ? "Add a reason for the admin discount."
                    : "Loading preview…"}
                </p>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, val, muted, bold, big }) {
  const cls = [
    "flex justify-between",
    muted && "text-[#676664]/70",
    bold && "font-medium text-[#282828]",
    big && "text-[13px]",
  ].filter(Boolean).join(" ");
  const sign = val < 0 ? "− " : "";
  return (
    <div className={cls}>
      <span>{label}</span>
      <span>{sign}${Math.abs(val).toFixed(2)}</span>
    </div>
  );
}
